import { prefersReducedMotion } from '@theme/utilities';

/**
 * Sitewide button micro-interaction: a minimal ripple that grows from the
 * press point on `.button` / `.button-secondary` (the shared primary/secondary
 * button classes rendered by snippets/button.liquid and used by add-to-cart,
 * forms, etc.). Delegated at the document level so it works for every button
 * without each section needing to wire anything up. Only ever animates
 * `transform`/`opacity` (see the `button-ripple` keyframes in base.css).
 * No-ops entirely under prefers-reduced-motion.
 */
function handlePointerDown(event) {
  if (prefersReducedMotion()) return;
  if (event.button != null && event.button !== 0) return;

  const button = /** @type {HTMLElement | null} */ (event.target)?.closest('.button, .button-secondary');
  if (!(button instanceof HTMLElement)) return;
  if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

  const rect = button.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  const size = Math.max(rect.width, rect.height);

  const ripple = document.createElement('span');
  ripple.className = 'button__ripple';
  ripple.style.left = `${x}px`;
  ripple.style.top = `${y}px`;
  ripple.style.width = `${size}px`;
  ripple.style.height = `${size}px`;

  ripple.addEventListener(
    'animationend',
    () => {
      ripple.remove();
    },
    { once: true }
  );

  button.appendChild(ripple);
  // Force layout before adding the class that starts the animation, so it
  // reliably plays even if the element was just created.
  ripple.getBoundingClientRect();
  ripple.classList.add('button__ripple--active');
}

document.addEventListener('pointerdown', handlePointerDown);
